import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { LoginResult } from '@mealdesk/shared';
import AuthShell, { fieldClass } from '../components/AuthShell.js';
import { Button } from '../components/ui.js';
import { ApiError, apiFetch } from '../lib/api.js';
import { LANGUAGES, setLanguage, useT } from '../lib/i18n.js';
import { useAuthStore } from '../stores/auth.js';

type Invitation = {
  company: string;
  newcomers: 'approval' | 'open';
  points: Array<{ id: string; name: string; site: string }>;
};

type Joined =
  | { status: 'pending'; company: string }
  | {
      status: 'active';
      company: string;
      session: Omit<LoginResult, 'refreshToken'> & { mustChangePassword?: boolean };
    };

const label = 'mb-2 block text-[13px] font-extrabold text-muted md:text-[14.5px]';
const hint = '-mt-2 mb-3.5 block text-[13px] leading-[1.45] text-subtle md:text-[14px]';

/**
 * What the company link opens.
 *
 * The person holding a phone in front of the canteen poster is the whole
 * audience, and a good share of them read Spanish first — so the language
 * switch is on this screen, not three screens in behind a profile page they
 * cannot reach yet.
 */
export default function JoinPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const { t, lang } = useT();

  const invitation = useQuery({
    queryKey: ['join', code],
    queryFn: () => apiFetch<Invitation>(`/api/join/code/${code ?? ''}`, { anonymous: true }),
    retry: false,
  });

  const [employeeNo, setEmployeeNo] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [pointId, setPointId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<string | null>(null);

  const languages = (
    <div className="mb-5 flex gap-2" role="group" aria-label={t('Language')}>
      {LANGUAGES.map((choice) => (
        <button
          key={choice.code}
          type="button"
          onClick={() => setLanguage(choice.code)}
          aria-pressed={lang === choice.code}
          className={`h-10 rounded-pill px-4 text-[13.5px] font-extrabold ${
            lang === choice.code
              ? 'bg-brand text-white'
              : 'border border-line-strong bg-white text-muted'
          }`}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );

  // The link went bad: replaced, switched off, or never real. One answer for
  // all three, because the fix is the same — ask for the current one.
  if (invitation.isError) {
    return (
      <AuthShell
        title={t('This link no longer works')}
        subtitle={t('Your company may have replaced it. Ask whoever manages accounts there for the current one.')}
        footer={
          <Link to="/signin" className="font-extrabold text-brand-ink">
            {t('Already have an account? Sign in')}
          </Link>
        }
      >
        {languages}
      </AuthShell>
    );
  }

  if (waiting) {
    return (
      <AuthShell
        title={t('Request sent')}
        subtitle={t('{company} will look at it. Once they approve it, sign in with the email and password you just chose.', {
          company: waiting,
        })}
        footer={
          <Link to="/signin" className="font-extrabold text-brand-ink">
            {t('Sign in')}
          </Link>
        }
      >
        <p className="rounded-tile bg-white px-4 py-3.5 text-[13.5px] leading-[1.5] text-muted md:text-[15px]">
          {t('Nothing else to do. If it is urgent, tell whoever manages accounts at your company that you have asked.')}
        </p>
      </AuthShell>
    );
  }

  const data = invitation.data;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await apiFetch<Joined>(`/api/join/code/${code ?? ''}`, {
        method: 'POST',
        anonymous: true,
        body: {
          email,
          name,
          password,
          ...(employeeNo.trim() ? { employeeNo: employeeNo.trim() } : {}),
          ...(pointId ? { pointId } : {}),
        },
      });

      if (result.status === 'pending') {
        setWaiting(result.company);
        return;
      }
      setSession(result.session);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error ? t(err.message) : t('Could not sign you up'),
      );
    } finally {
      setBusy(false);
    }
  };

  const ready =
    email.includes('@') && name.trim().length > 0 && password.length >= 10 && !busy && !!data;

  return (
    <AuthShell
      title={data ? t('Join {company}', { company: data.company }) : t('Join your company')}
      subtitle={
        data?.newcomers === 'open'
          ? t('Sign up and you can order straight away.')
          : t('If your employee number and email match what your company has on file, you can order straight away. If not, your company approves you first.')
      }
      footer={
        <Link to="/signin" className="font-extrabold text-brand-ink">
          {t('Already have an account? Sign in')}
        </Link>
      }
    >
      {languages}

      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void submit();
        }}
      >
        <label htmlFor="employeeNo" className={label}>
          {t('Employee number')}
        </label>
        <input
          id="employeeNo"
          autoComplete="off"
          value={employeeNo}
          onChange={(e) => setEmployeeNo(e.target.value)}
          className={fieldClass}
        />
        <span className={hint}>{t('The number on your badge. Leave it blank if you do not have one.')}</span>

        <label htmlFor="email" className={label}>
          {t('Email')}
        </label>
        <input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={fieldClass}
        />
        <span className={hint}>{t('The one your company has for you, if it has one.')}</span>

        <label htmlFor="name" className={label}>
          {t('Name')}
        </label>
        <input
          id="name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={fieldClass}
        />

        <label htmlFor="password" className={label}>
          {t('Password')}
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={fieldClass}
        />
        <span className={hint}>{t('At least 10 characters. You stay signed in for 90 days.')}</span>

        {data && data.points.length > 0 && (
          <>
            <label htmlFor="point" className={label}>
              {t('Where do you pick up lunch?')}
            </label>
            <select
              id="point"
              value={pointId}
              onChange={(e) => setPointId(e.target.value)}
              className={fieldClass}
            >
              <option value="">{t('Decide later')}</option>
              {data.points.map((point) => (
                <option key={point.id} value={point.id}>
                  {point.site} · {point.name}
                </option>
              ))}
            </select>
          </>
        )}

        <Button type="submit" disabled={!ready}>
          {busy ? `${t('Join')}…` : t('Join')}
        </Button>

        {error && (
          <p className="mt-3.5 rounded-tile bg-danger-soft px-4 py-3.5 text-[13.5px] font-bold text-danger md:text-[15px]">
            {error}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
